// SEQ-180 E19: monthly trend of profit % and overtime % revenue, toggled
// between the whole (filtered) company and a single selected project.
//
// CONTRACT CHANGE (round 3 addendum): the RPC's groupBy no longer offers a
// combined project+month grain, so a single project's monthly trend cannot
// be sliced out of data already on the page. Picking a project in the "View"
// selector below triggers its own on-demand call
// (usePnlAnalyticsProjectTrend, groupBy: "month", projectIds: [id]).
//
// SEQ-180 legibility follow-up (15 September 2026). Two defects.
//
//   NO EMPTY STATE THAT FIRED. The test was `chartData.length === 0`, and on a
//   range with no figures the rows still arrive: one per month, every value
//   null. So the card drew an empty box with a legend under it, which reads as
//   a chart that failed rather than as a range with nothing in it.
//
//   THE LEGEND FAILED AA. Recharts colours legend TEXT with the series colour,
//   which put "Overtime % revenue" on screen at 2.15:1 and "Profit %" at
//   2.54:1, both at 12px. The swatch beside each label is a graphic and keeps
//   the series colour, where 3:1 is the bar it has to clear. The words are
//   words.
import { useMemo, useState, type ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ReferenceLine, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { LineChart as LineChartIcon, Loader2 } from "lucide-react";
import { usePnlAnalyticsProjectTrend, type PnlAnalyticsQueryFilters } from "../../features/pnlAnalytics/usePnlAnalytics";
import { monthRowsToRollups, type MonthRollup } from "../../features/pnlAnalytics/rollup";
import { pnlChartCardStyle, formatMonthLabel, formatPnlMetric } from "../../features/pnlAnalytics/pnlAnalyticsFormat";
import { hasPlottableValues } from "../../features/pnlAnalytics/chartEmptiness";
import { chartAnimation } from "../../features/pnlAnalytics/pnlMotion";
import { PnlChartEmpty, PnlChartHead } from "./PnlChartFrame";
import { pnlLegendLabel } from "./pnlLegendLabel";
import "./PnlWidgetCharts.css";

const COMPANY_VIEW = "__company__";

const CHART_HEIGHT = 300;

/** The two series this chart draws, by the field each one reads. */
const SERIES_KEYS = ["profitPercent", "overtimePctRevenue"];

interface PnlMonthlyTrendChartProps {
  /** The page's active date/project/status filters, reused as-is for company view and narrowed to one project for the drill call. */
  filters: PnlAnalyticsQueryFilters;
  companyByMonth: MonthRollup[];
  projectOptions: { id: string; name: string }[];
  /** SEQ-240: the "How is this worked out" trigger beside the title. */
  info?: ReactNode;
}

export function PnlMonthlyTrendChart({ filters, companyByMonth, projectOptions, info }: PnlMonthlyTrendChartProps) {
  const reduce = useReducedMotion() ?? false;
  const [selectedProjectId, setSelectedProjectId] = useState<string>(COMPANY_VIEW);
  const drilledProjectId = selectedProjectId === COMPANY_VIEW ? null : selectedProjectId;

  const trend = usePnlAnalyticsProjectTrend(filters, drilledProjectId);

  const monthly = useMemo(() => {
    if (drilledProjectId === null) return companyByMonth;
    return monthRowsToRollups(trend.data?.rows ?? []);
  }, [drilledProjectId, companyByMonth, trend.data]);

  const chartData = useMemo(
    () =>
      monthly.map((m) => ({
        label: formatMonthLabel(m.periodMonth),
        profitPercent: m.profitPercent,
        overtimePctRevenue: m.overtimePctRevenue,
      })),
    [monthly],
  );

  const isDrillLoading = drilledProjectId !== null && trend.loading;
  const selectedName = projectOptions.find((p) => p.id === drilledProjectId)?.name ?? null;
  const subject = selectedName ?? "every project added together";
  const caption =
    selectedName === null
      ? "One point per month, every project added together. Both lines are shares of revenue, so they can be read against each other."
      : `One point per month for ${selectedName}. Both lines are shares of that project's revenue.`;

  return (
    <div style={pnlChartCardStyle} className="pnl-chart-card">
      <PnlChartHead
        icon={<LineChartIcon size={18} aria-hidden="true" style={{ color: "var(--seq-blue)" }} />}
        title="Monthly trend"
        info={info}
        caption={caption}
      >
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 500, color: "var(--text-muted)" }}>
          View
          <select
            className="form-input"
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
            style={{ padding: "4px 8px", fontSize: 12, width: "auto", maxWidth: 200 }}
          >
            <option value={COMPANY_VIEW}>Company total</option>
            {projectOptions.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </label>
      </PnlChartHead>

      {isDrillLoading ? (
        <div
          className="pnl-chart-body"
          style={{ height: CHART_HEIGHT, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: "var(--text-secondary)" }}
        >
          <Loader2 size={16} className="spinner" aria-hidden="true" /> Loading this project's trend...
        </div>
      ) : trend.error && drilledProjectId !== null ? (
        <div
          className="pnl-chart-body"
          role="alert"
          style={{
            height: CHART_HEIGHT, display: "flex", alignItems: "center", justifyContent: "center",
            padding: "0 1rem", textAlign: "center", color: "var(--text-secondary)",
          }}
        >
          Could not load this project's trend: {trend.error}
        </div>
      ) : !hasPlottableValues(chartData, SERIES_KEYS) ? (
        <PnlChartEmpty height={CHART_HEIGHT} />
      ) : (
        <motion.div
          className="pnl-chart-body"
          style={{ width: "100%", height: CHART_HEIGHT }}
          // Keyed on the subject so switching from the company to one project
          // redraws rather than sliding one set of months into another.
          key={selectedProjectId}
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: reduce ? 0 : 0.2 }}
        >
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "var(--text-secondary)" }} />
              <YAxis tickFormatter={(v: number) => `${v}%`} tick={{ fontSize: 12, fill: "var(--text-secondary)" }} />
              <Tooltip
                formatter={((value: number | null, name: string) => [formatPnlMetric(value, "percent"), name]) as never}
                // The month alone does not say whose month it is, and this
                // chart has a subject the reader chose two controls ago.
                labelFormatter={(label) => `${label}, ${subject}`}
                contentStyle={{ borderRadius: 12, border: "1px solid var(--border-color)", fontSize: "0.85rem" }}
              />
              {/* A month that lost money crosses this line. Without it a
                  negative month reads as a small one. */}
              <ReferenceLine y={0} stroke="var(--text-secondary)" strokeWidth={1} />
              <Legend wrapperStyle={{ fontSize: 12 }} formatter={pnlLegendLabel as never} />
              <Line
                type="monotone"
                dataKey="profitPercent"
                name="Profit %"
                stroke="var(--color-success)"
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5, strokeWidth: 2 }}
                connectNulls
                {...chartAnimation(reduce, 0)}
              />
              <Line
                type="monotone"
                dataKey="overtimePctRevenue"
                name="Overtime % revenue"
                stroke="var(--color-warning)"
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5, strokeWidth: 2 }}
                connectNulls
                {...chartAnimation(reduce, 1)}
              />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>
      )}
    </div>
  );
}
